import { ActivatedRoute } from '@angular/router';
import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Observable, of } from 'rxjs';
import { catchError, map, shareReplay, switchMap, take, tap } from 'rxjs/operators';
import { Subject } from 'rxjs';
import {
  TranslationContextMode,
  TranslationContextService,
} from '@services/user/translation-context.service';
import { EditableLabelsService } from '@services/editable-labels/editable-labels.service';
import { EditableLabelsMap } from '@models/EditableLabels/editable-labels.model';

@Injectable({ providedIn: 'root' })
export class LanguageService {
  private flagPath = 'assets/flags/4x3/';

  private languageChangedSubject = new Subject<string>();
  public languageChanged$ = this.languageChangedSubject.asObservable();

  private allOverrides$?: Observable<Record<string, EditableLabelsMap>>;

  public allLangs = [
    { flag: this.flagPath + 'it.svg', lang: 'it', label: 'italiano' },
    { flag: this.flagPath + 'gb.svg', lang: 'en', label: 'english' },
    { flag: this.flagPath + 'de.svg', lang: 'deu', label: 'deutsch' },
    { flag: this.flagPath + 'fr.svg', lang: 'fr', label: 'francaise' },
    { flag: this.flagPath + 'es.svg', lang: 'es', label: 'spanish' },
  ];

  constructor(
    private translate: TranslateService,
    private translationContext: TranslationContextService,
    private editableLabelsService: EditableLabelsService,
  ) {
    this.translate.setDefaultLang('en'); // Lingua predefinita
    this.translate.use('en'); // Imposta la lingua iniziale
  }

  get currentLang(): string {
    return this.translate.currentLang || this.translate.getDefaultLang();
  }

  setAuthenticated() {
    this.applyTranslationContext('auth');
  }

  setExternalTenant(tenant: string) {
    this.applyTranslationContext('url', `/${encodeURIComponent(tenant)}`);
  }

  setExternalProcessToken(processToken: string) {
    this.applyTranslationContext('url', `?processToken=${encodeURIComponent(processToken)}`);
  }

  private applyTranslationContext(mode: TranslationContextMode, urlSuffix = '') {
    if (!this.translationContext.setContext(mode, urlSuffix)) return;

    // cambiato tenant: gli override in cache sono di quello precedente
    this.allOverrides$ = undefined;

    const current = this.currentLang;
    this.translationContext
      .staleEmbeddedLangs()
      .filter((lang) => lang !== current)
      .forEach((lang) => {
        this.translate.resetLang(lang);
        this.translationContext.forget(lang);
      });
  }

  changeWithRoute(route: ActivatedRoute) {
    let lang = route.snapshot.paramMap.get('lang');
    if (!lang) lang = route.snapshot.queryParamMap.get('lang');
    if (lang) this.changeLanguage(lang);
  }

  changeLanguage(lang: string) {
    this.changeLanguageAsync(lang).subscribe();
  }

  changeLanguageAsync(lang: string): Observable<void> {
    lang = this.normalizeLang(lang);
    return this.translate.use(lang).pipe(
      switchMap(() => this.applyOverrides$(lang)),
      tap(() => setTimeout(() => this.languageChangedSubject.next(lang), 500)),
    );
  }

  private normalizeLang(lang: string): string {
    if (lang == 'italiano') return 'it';
    if (lang == 'english') return 'en';
    if (lang == 'francaise') return 'fr';
    if (lang == 'deutsch') return 'deu';
    if (lang == 'spanish') return 'es';
    return lang;
  }

  private applyOverrides$(lang: string): Observable<void> {
    if (!this.translationContext.needsOverrides(lang)) return of(undefined);

    return this.translationContext.isAuthMode ? this.mergeAuthOverrides$(lang) : this.reloadFromLoader$(lang);
  }

  /**
   * Applicare una lingua alla volta, e solo dopo che è stata caricata: `setTranslation` su
   * una lingua mai caricata la definirebbe, e ngx-translate non scaricherebbe più il json
   * statico, lasciando a schermo i soli override.
   */
  private mergeAuthOverrides$(lang: string): Observable<void> {
    if (!this.allOverrides$) {
      this.allOverrides$ = this.editableLabelsService.getAllOverrides().pipe(shareReplay(1));
    }

    return this.allOverrides$.pipe(
      take(1),
      tap((byLanguage) => {
        const overrides = byLanguage?.[lang.toUpperCase()];
        if (overrides && Object.keys(overrides).length > 0) {
          this.translate.setTranslation(lang, overrides, true);
        }
        this.translationContext.markAuthApplied(lang);
      }),
      map(() => undefined),
      catchError(() => {
        // butto la cache: senza, shareReplay rigiocherebbe l'errore per tutta la sessione
        this.allOverrides$ = undefined;
        return of(undefined);
      }),
    );
  }

  /**
   * resetLang azzera la memoizzazione; setTranslation subito dopo emette onTranslationChange
   * così le pipe già renderizzate si aggiornano (reloadLang da solo non emette nulla).
   */
  private reloadFromLoader$(lang: string): Observable<void> {
    return this.translate.currentLoader.getTranslation(lang).pipe(
      take(1),
      tap((raw) => {
        this.translate.resetLang(lang);
        this.translate.setTranslation(lang, raw, false);
      }),
      map(() => undefined),
      catchError(() => of(undefined)),
    );
  }

  instantTranslate(key: string, language: string, params?: object): Observable<string> {
    const langInfo = this.allLangs.find((l) => l.label === language || l.lang === language);
    const targetLang = langInfo ? langInfo.lang : this.translate.getDefaultLang();

    if (!langInfo) {
      console.warn(`Language '${language}' not found. Falling back to default '${targetLang}'.`);
    }

    return this.translate.use(targetLang).pipe(map(() => this.translate.instant(key, params)));
  }

  reeload() {
    const lang = this.translate.currentLang;
    this.translate.reloadLang(lang).subscribe(() => {
      this.translate.use(lang);
    });
  }
}
